/**
 * Splash component.
 */
import AdUtils from '@shared/utils/ads';
import ApiConfig from '@api';
import AppConfig from '@app';
import Background from '@components/atoms/images/splashBackground';
import CmpUtils from '@shared/utils/cmp';
import Container from '@components/atoms/container/splashContainer';
import Focus from '@shared/types/enums/focus';
import Logo from '@components/atoms/images/splashLogo';
import Package from '../../../../package.json';
import Pages from '@pages';
import Sections from '@shared/types/enums/sections';
import Version from '@components/atoms/text/splashVersion';
import { DURATION } from '../../../../shared/types/constants/animations';
import { appSelector } from '@shared/store/slices/app.slice';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { setAds } from '@shared/store/slices/ads.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Constants
 * SPLASH_TIMEOUT: The minumum time the splash screen should be displayed. (2 seconds)
 */
const SPLASH_TIMEOUT = 2 * 1000;

/**
 * Splash section component.
 */
export default function Splash() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const {
    status: {
      splash: { ads, display },
    },
  } = useAppSelector(appSelector);
  const { focus } = useAppSelector(focusSelector);
  const { route } = useAppSelector(routerSelector);

  // State
  const [cmp, setCmp] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [render, setRender] = useState(true);

  // Refs
  const focusRef = useRef(focus);
  focusRef.current = focus;

  // Memos
  const version = useMemo(() => {
    const dislpayRC =
      (AppConfig.environment === 'dev' ||
        ApiConfig.environment === 'stage' ||
        window.location.href.includes('stage') ||
        window.location.href.includes('preproduction')) &&
      Package.rc;
    return `${Package.version}${dislpayRC ? `.${Package.rc}` : ''}`;
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  // Set resolved state
  useEffect(() => {
    let cmpInterval: ReturnType<typeof setInterval> | undefined = undefined;
    let resolveTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    if (!resolved && !cmp && !display) {
      resolveTimeout = setTimeout(() => {
        setResolved(true);
        if (route.Page !== Pages.LANGUAGES) {
          dispatch(setFocus(Focus.MENU));
          if (ads) {
            const adSource = AdUtils.getSource(Sections.START_APP);
            dispatch(setAds(adSource));
          }
        }
      }, SPLASH_TIMEOUT);
    }
    cmpInterval = setInterval(() => {
      setCmp(CmpUtils.isOpen());
    }, 1000);
    return () => {
      clearInterval(cmpInterval);
      clearTimeout(resolveTimeout);
    };
  }, [ads, cmp, display, resolved]);

  useEffect(() => {
    let destroyTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    if (resolved) {
      destroyTimeout = setTimeout(() => {
        setRender(false);
      }, DURATION.SPLASH);
    }
    return () => {
      clearTimeout(destroyTimeout);
    };
  }, [resolved]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return render ? (
    <Container display={!resolved} duration={!resolved ? 0 : DURATION.SPLASH}>
      <Background />
      <Version>{version}</Version>
      <Logo duration={DURATION.SPLASH} />
    </Container>
  ) : null;
}
